import { zodResolver } from "@hookform/resolvers/zod";
import {
	Alert,
	Button,
	Group,
	Loader,
	Modal,
	NumberInput,
	Paper,
	Stack,
	Switch,
	Table,
	TextInput,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import {
	createDiscountPosition,
	deleteDiscountPosition,
	discountKeys,
	listDiscountPositions,
	updateDiscountPosition,
} from "@/features/discounts/api/api";
import {
	type PositionFormValues,
	positionSchema,
} from "@/features/discounts/lib/position-schema";
import { showDiscountError } from "@/features/discounts/lib/use-discount-error";
import { describeApiErrorSync } from "@/shared/api/error-message";
import { ConfirmDialog } from "@/shared/ui/confirm-dialog";

type PositionEditor = {
	id?: string;
	defaults: PositionFormValues;
};

export function PositionsTab() {
	const { t } = useTranslation();
	const queryClient = useQueryClient();
	const [editor, setEditor] = useState<PositionEditor | null>(null);
	const [pendingDelete, setPendingDelete] = useState<{
		id: string;
		name: string;
	} | null>(null);

	const listQuery = useQuery({
		queryKey: discountKeys.positions({ limit: 100 }),
		queryFn: () => listDiscountPositions({ limit: 100 }),
	});

	const saveMutation = useMutation({
		mutationFn: (input: { id?: string; values: PositionFormValues }) => {
			if (input.id) {
				return updateDiscountPosition(input.id, {
					name: input.values.name,
					sortOrder: input.values.sortOrder,
					isActive: input.values.isActive,
				});
			}
			return createDiscountPosition({
				name: input.values.name,
				sortOrder: input.values.sortOrder,
			});
		},
		onSuccess: async () => {
			notifications.show({
				color: "green",
				message: t("discounts.positionSaved"),
			});
			setEditor(null);
			await queryClient.invalidateQueries({ queryKey: discountKeys.all });
		},
		onError: showDiscountError,
	});

	const deleteMutation = useMutation({
		mutationFn: deleteDiscountPosition,
		onSuccess: async () => {
			setPendingDelete(null);
			notifications.show({
				color: "red",
				message: t("discounts.positionDeleted"),
			});
			await queryClient.invalidateQueries({ queryKey: discountKeys.all });
		},
		onError: showDiscountError,
	});

	return (
		<Paper p="md" radius="lg" withBorder>
			<Group justify="flex-end" mb="md">
				<Button
					onClick={() =>
						setEditor({
							defaults: { name: "", sortOrder: 0, isActive: true },
						})
					}
				>
					{t("discounts.addPosition")}
				</Button>
			</Group>
			{listQuery.error ? (
				<Alert color="red" title={t("common.loadError")}>
					{describeApiErrorSync(listQuery.error)}
				</Alert>
			) : null}
			{listQuery.isLoading ? (
				<Loader />
			) : (
				<Table.ScrollContainer type="native" minWidth={560}>
					<Table verticalSpacing="sm">
						<Table.Thead>
							<Table.Tr>
								<Table.Th>{t("discounts.titleField")}</Table.Th>
								<Table.Th>{t("discounts.sortOrder")}</Table.Th>
								<Table.Th>{t("common.active")}</Table.Th>
								<Table.Th />
							</Table.Tr>
						</Table.Thead>
						<Table.Tbody>
							{listQuery.data?.data.map((row) => (
								<Table.Tr key={row.id}>
									<Table.Td>{row.name}</Table.Td>
									<Table.Td>{row.sortOrder}</Table.Td>
									<Table.Td>
										<Switch
											checked={row.isActive}
											onChange={(event) =>
												updateDiscountPosition(row.id, {
													isActive: event.currentTarget.checked,
												})
													.then(() =>
														queryClient.invalidateQueries({
															queryKey: discountKeys.all,
														}),
													)
													.catch(showDiscountError)
											}
										/>
									</Table.Td>
									<Table.Td>
										<Group gap="xs" justify="flex-end">
											<Button
												size="xs"
												variant="light"
												onClick={() =>
													setEditor({
														id: row.id,
														defaults: {
															name: row.name,
															sortOrder: row.sortOrder,
															isActive: row.isActive,
														},
													})
												}
											>
												{t("common.edit")}
											</Button>
											<Button
												size="xs"
												color="red"
												variant="light"
												onClick={() =>
													setPendingDelete({ id: row.id, name: row.name })
												}
											>
												{t("common.delete")}
											</Button>
										</Group>
									</Table.Td>
								</Table.Tr>
							))}
						</Table.Tbody>
					</Table>
				</Table.ScrollContainer>
			)}
			<Modal
				opened={Boolean(editor)}
				onClose={() => setEditor(null)}
				title={t("discounts.position")}
				centered
			>
				{editor ? (
					<PositionEditorForm
						key={editor.id ?? "new"}
						defaults={editor.defaults}
						existingId={editor.id}
						saving={saveMutation.isPending}
						onCancel={() => setEditor(null)}
						onSubmit={(values) =>
							saveMutation.mutate({ id: editor.id, values })
						}
					/>
				) : null}
			</Modal>

			<ConfirmDialog
				opened={Boolean(pendingDelete)}
				title={t("discounts.confirmDeletePositionTitle")}
				body={t("discounts.confirmDeletePositionBody", {
					name: pendingDelete?.name ?? "",
				})}
				loading={deleteMutation.isPending}
				onCancel={() => setPendingDelete(null)}
				onConfirm={() => {
					if (!pendingDelete) {
						return;
					}
					deleteMutation.mutate(pendingDelete.id);
				}}
			/>
		</Paper>
	);
}

function PositionEditorForm({
	defaults,
	existingId,
	saving,
	onCancel,
	onSubmit,
}: {
	defaults: PositionFormValues;
	existingId?: string;
	saving: boolean;
	onCancel: () => void;
	onSubmit: (values: PositionFormValues) => void;
}) {
	const { t } = useTranslation();
	const { control, handleSubmit } = useForm<PositionFormValues>({
		resolver: zodResolver(positionSchema),
		defaultValues: defaults,
	});

	return (
		<form onSubmit={handleSubmit(onSubmit)}>
			<Stack>
				<Controller
					name="name"
					control={control}
					render={({ field, fieldState }) => (
						<TextInput
							{...field}
							label={t("discounts.titleField")}
							error={fieldState.error?.message}
						/>
					)}
				/>
				<Controller
					name="sortOrder"
					control={control}
					render={({ field }) => (
						<NumberInput
							label={t("discounts.sortOrder")}
							min={0}
							value={field.value}
							onChange={(value) =>
								field.onChange(typeof value === "number" ? value : 0)
							}
						/>
					)}
				/>
				{existingId ? (
					<Controller
						name="isActive"
						control={control}
						render={({ field }) => (
							<Switch
								label={t("common.active")}
								checked={field.value}
								onChange={(event) =>
									field.onChange(event.currentTarget.checked)
								}
							/>
						)}
					/>
				) : null}
				<Group justify="flex-end">
					<Button variant="default" type="button" onClick={onCancel}>
						{t("common.cancel")}
					</Button>
					<Button type="submit" loading={saving}>
						{t("common.save")}
					</Button>
				</Group>
			</Stack>
		</form>
	);
}
